import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LayoutDashboard } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created successfully');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => e?.msg || JSON.stringify(e)).join('. ');
      } else if (detail?.msg) {
        msg = detail.msg;
      } else {
        msg = error.message || 'Failed to create account. Please try again.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Dark branding */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-14 relative overflow-hidden" style={{ background: '#0A3622' }}>
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-16">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="font-heading text-lg font-semibold text-white">SnuggleMind</span>
          </div>

          <div className="mb-8 p-6 rounded-xl" style={{ background: '#2D3330', border: '1px solid #3C3C3E' }}>
            <div className="flex justify-between items-center mb-4">
              <span className="text-[10px] font-body uppercase tracking-[0.2em]" style={{ color: '#9CA3AF' }}>AI Analysis</span>
              <span className="px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full" style={{ background: 'rgba(224,122,95,0.15)', color: '#0A3622' }}>Pattern</span>
            </div>
            <p className="text-sm font-body mb-4" style={{ color: '#9CA3AF' }}>
              "Based on vendor name 'AWS' and recurring monthly amount, this appears to be a cloud hosting expense."
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#3C3C3E' }}>
                <div className="h-full w-4/5 rounded-full" style={{ background: '#2D7A5D' }}></div>
              </div>
              <span className="text-sm font-mono" style={{ color: '#2D7A5D' }}>85%</span>
            </div>
          </div>
          <div className="p-6 rounded-xl" style={{ background: '#2D3330', border: '1px solid #3C3C3E' }}>
            <div className="flex justify-between items-center mb-4">
              <span className="text-[10px] font-body uppercase tracking-[0.2em]" style={{ color: '#9CA3AF' }}>Context Override</span>
              <span className="px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full" style={{ background: 'rgba(198,156,109,0.15)', color: '#D98A4A' }}>Context</span>
            </div>
            <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>
              "Company policy requires review for all cloud service expenses over $5,000."
            </p>
          </div>
        </div>

        <div className="relative z-10" />
        <div className="absolute top-20 right-0 w-96 h-96 opacity-[0.06]" style={{ background: 'radial-gradient(circle, #D98A4A 0%, transparent 70%)' }}></div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 xl:px-24" style={{ background: '#F7F6F2' }}>
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-12 lg:hidden">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#D98A4A' }}>
              <LayoutDashboard size={20} className="text-white" />
            </div>
            <span className="font-heading text-xl font-semibold italic" style={{ color: '#1A1A1A' }}>AI Snugglemind</span>
          </div>

          <h1 className="font-heading text-4xl font-medium tracking-tight mb-2" style={{ color: '#1A1A1A' }}>Create account</h1>
          <p className="font-body mb-8" style={{ color: '#4B5563' }}>Start managing your bookkeeping with AI assistance</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#4B5563' }}>Full Name</Label>
              <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" required data-testid="register-name-input" className="h-12 rounded-lg font-body" style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#4B5563' }}>Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="register-email-input" className="h-12 rounded-lg font-body" style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-body uppercase tracking-[0.15em] font-semibold" style={{ color: '#4B5563' }}>Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 characters" required data-testid="register-password-input" className="h-12 rounded-lg font-body" style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }} />
            </div>

            <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full h-12 rounded-full font-body font-medium text-white transition-colors duration-200" style={{ background: '#0A3622' }} >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: '1px solid #E5E3DB' }}></div></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="px-4 font-body tracking-[0.15em]" style={{ background: '#F7F6F2', color: '#9CA3AF' }}>Or continue with</span></div>
          </div>

          <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-register-button" className="w-full h-12 rounded-full font-body font-medium transition-colors duration-200" style={{ borderColor: '#1A1A1A', color: '#1A1A1A', background: 'transparent' }}>
            <svg className="mr-2" width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Google
          </Button>

          <p className="mt-8 text-center text-sm font-body" style={{ color: '#4B5563' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-medium hover:underline" style={{ color: '#0A3622' }} data-testid="login-link">Sign in</Link>
          </p>

          <div className="mt-8 flex items-center justify-center gap-4 font-body text-xs" style={{ color: '#9CA3AF' }}>
            <Link to="/terms" className="hover:underline">Terms of Use</Link>
            <span>&middot;</span>
            <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
            <span>&middot;</span>
            <Link to="/subscription-agreement" className="hover:underline">Subscription Agreement</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
